import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { CheckCircle2, Loader2, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function AuditForm() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    property_type: 'appartement',
    message: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!formData.name || !formData.email || !formData.phone || !formData.address) {
      setFormError(t('audit.errorIncomplete'));
      return;
    }

    setLoading(true);

    try {
      if (!supabase) throw new Error("Supabase is not configured yet.");

      const { error } = await supabase.from('audits').insert([
        formData
      ]);

      if (error) {
        console.error('Erreur Supabase:', error);
        setFormError(t('audit.errorSend'));
      } else {
        setSuccess(true);
        try {
          if (typeof window !== 'undefined' && (window as any).fbq) {
            (window as any).fbq('track', 'Lead');
          }
        } catch (e) {
          console.warn('FB Pixel Error:', e);
        }
      }
    } catch (err) {
      console.error('Error submitting audit:', err);
      alert(t('audit.errorAlert'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-12">
        <CheckCircle2 className="w-20 h-20 text-gold-500 mb-6" />
        <h3 className="text-2xl font-serif text-black-deep mb-2">{t('audit.successTitle')}</h3>
        <p className="text-gray-500 font-light">{t('audit.successDesc')}</p>
      </div>
    );
  }

  return (
    <>
      <h2 className="text-3xl font-serif text-black-deep mb-4 text-center">{t('audit.formTitle')}</h2>
      <p className="text-gray-500 text-center mb-6 font-light">{t('audit.formDesc')}</p>
      
      <div className="mb-10 bg-cream-100/50 border border-gold-200/50 p-4 rounded-sm flex items-start gap-3 max-w-2xl mx-auto">
        <MapPin className="text-gold-500 w-5 h-5 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-black-deep/80 font-light leading-relaxed">
          <strong className="font-semibold">{t('common.exclusiveSectorPrefix')}</strong> {t('common.exclusiveSectorSuffix')}
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="audit-name" className="block text-xs uppercase tracking-wider text-gray-500 mb-2">{t('audit.labelName')}</label>
            <input id="audit-name" required type="text" className="w-full bg-cream-50 border border-gray-200 text-black-deep px-4 py-3 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors"
              value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})}
            />
          </div>
          <div>
            <label htmlFor="audit-phone" className="block text-xs uppercase tracking-wider text-gray-500 mb-2">{t('audit.labelPhone')}</label>
            <input id="audit-phone" required type="tel" className="w-full bg-cream-50 border border-gray-200 text-black-deep px-4 py-3 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors"
              value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})}
            />
          </div>
        </div>

        <div>
          <label htmlFor="audit-email" className="block text-xs uppercase tracking-wider text-gray-500 mb-2">{t('audit.labelEmail')}</label>
          <input id="audit-email" required type="email" className="w-full bg-cream-50 border border-gray-200 text-black-deep px-4 py-3 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors"
            value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})}
          />
        </div>

        <div>
          <label htmlFor="audit-address" className="block text-xs uppercase tracking-wider text-gray-500 mb-2">{t('audit.labelAddress')}</label>
          <input id="audit-address" required type="text" className="w-full bg-cream-50 border border-gray-200 text-black-deep px-4 py-3 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors"
            value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})}
          />
        </div>

        <div>
          <label htmlFor="audit-property" className="block text-xs uppercase tracking-wider text-gray-500 mb-2">{t('audit.labelProperty')}</label>
          <select id="audit-property" className="w-full bg-cream-50 border border-gray-200 text-black-deep px-4 py-3 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors"
            value={formData.property_type} onChange={e => setFormData({...formData, property_type: e.target.value})}
          >
            <option value="appartement">{t('audit.propAppart')}</option>
            <option value="maison">{t('audit.propHouse')}</option>
            <option value="hotel_particulier">{t('audit.propMansion')}</option>
          </select>
        </div>

        <div>
          <label htmlFor="audit-message" className="block text-xs uppercase tracking-wider text-gray-500 mb-2">{t('audit.labelMessage')}</label>
          <textarea id="audit-message" rows={4} className="w-full bg-cream-50 border border-gray-200 text-black-deep px-4 py-3 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors"
            value={formData.message} onChange={e => setFormData({...formData, message: e.target.value})}
          ></textarea>
        </div>

        {formError && (
          <div className="text-red-600 text-sm font-bold bg-red-50 border border-red-200 p-4 rounded-sm text-center">
            {formError}
          </div>
        )}

        <button disabled={loading} type="submit" className="w-full flex items-center justify-center bg-gradient-to-r from-black-deep to-gray-900 text-white shadow-[0_8px_20px_rgba(10,10,10,0.3)] hover:shadow-[0_12px_25px_rgba(10,10,10,0.5)] hover:-translate-y-0.5 rounded-full transition-all duration-300 mt-4 disabled:opacity-70 py-4 font-bold uppercase tracking-widest text-sm">
          {loading ? <Loader2 className="animate-spin mr-2" /> : null}
          {loading ? t('audit.btnLoading') : t('audit.btnSubmit')}
        </button>
      </form>
    </>
  );
}
